/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import {
  canClientsConnectToCluster,
  isPersistenceEnabled,
  productEditionNameAndMajorVersion,
} from '@app/domain/cluster';
import { isNebulaInStatus } from '@app/domain/clusters';
import { createSelector } from '@ngrx/store';
import { isCluster2x as is2x, isCluster3x as is3x } from '@shared/domain/cluster';
import { ClusterStatus } from '@shared/types/cluster';
import { memoize } from 'lodash-es';
import { currentClusterInfo, selectUrl } from './app.selectors';

export const currentClusterConfiguration = createSelector(currentClusterInfo, (cluster) =>
  !!cluster && is2x(cluster) ? cluster?.info?.configuration : undefined,
);
export const areMetricsEnabled = createSelector(currentClusterConfiguration, (config) => config?.metricsEnabled);
export const isTracingEnabled = createSelector(currentClusterConfiguration, (config) => config?.tracingEnabled);
export const isComputeEnabled = createSelector(currentClusterConfiguration, (config) => config?.computeEnabled);
export const isRunningQueriesEnabled = createSelector(
  currentClusterConfiguration,
  (config) => config?.runningQueriesEnabled,
);

export const isMetricsDisabledBannerVisible = createSelector(
  areMetricsEnabled,
  selectUrl,
  (metricsEnabled, url) => metricsEnabled === false && !!url?.match(/\/(monitoring-dashboard|alerting)\//),
);

export const isCluster3xSelector = createSelector(currentClusterInfo, (cluster) => !!cluster && is3x(cluster));
export const isCluster2xSelector = createSelector(currentClusterInfo, (cluster) => !!cluster && is2x(cluster));

export const canClientsConnectToClusterSelector = createSelector(currentClusterInfo, (cluster) =>
  cluster ? canClientsConnectToCluster(cluster) : false,
);

export const isCurrentClusterPersistenceEnabled = createSelector(currentClusterInfo, (cluster) =>
  !cluster ? false : isPersistenceEnabled(cluster),
);

export const currentClusterDrDcId = createSelector(currentClusterInfo, (cluster) =>
  cluster && is2x(cluster) ? cluster.info?.drDcId : null,
);

export const currentClusterIsDrSender = createSelector(currentClusterInfo, (cluster) =>
  cluster && is2x(cluster) ? cluster.info?.configuration.drSender : false,
);

export const currentClusterIsDrReceiver = createSelector(currentClusterInfo, (cluster) =>
  cluster && is2x(cluster) ? cluster.info?.configuration.drReceiver : false,
);

export const currentManagedClusterInNebulaStatus = memoize((status: ClusterStatus) =>
  createSelector(currentClusterInfo, (cluster) => !!cluster && isNebulaInStatus(cluster, [status])),
);

export const currentClusterProductNameAndMajorVersionSelector = createSelector(currentClusterInfo, (cluster) => {
  return cluster?.edition ? productEditionNameAndMajorVersion(cluster.edition) : undefined;
});
